import React from 'react';
import {
  FiActivity,
  FiCheckCircle,
  FiStar,
  FiHeart,
  FiAlertTriangle,
  FiAlertCircle,
  FiLoader,
  FiShield,
  FiArrowRight,
  FiTrendingDown,
  FiDroplet,
  FiInfo,
} from 'react-icons/fi';
import LaborScoreTrendsChart from './LaborScoreTrendsChart';
import ScoreDistributionChart from './ScoreDistributionChart';
import ScoreBandMigrationChart from './ScoreBandMigrationChart';
import FinancialHealthRadarChart from './FinancialHealthRadarChart';
import LiveMetricCard from './LiveMetricCard';
import DataSourceNotConnected from './DataSourceNotConnected';
import DataSufficiencyWrapper from './DataSufficiencyWrapper';
import DataSourceBadge from './DataSourceBadge';
import ExecutiveSummaryPanel from '../common/ExecutiveSummaryPanel';
import { useInsightsData } from '../../../hooks/useInsightsData';
import { NOT_CONNECTED_REGISTRY } from '../../../types/insightsTypes';

const FinancialHealth: React.FC = () => {
  const { data, loading, error } = useInsightsData();
  const fh = data?.financial_health;
  const phi = data?.overview?.portfolio_health_index;

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <FiLoader className="animate-spin text-slate-400 mr-3" size={20} />
        <span className="text-slate-500">Loading financial health data…</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700 font-medium">Failed to load insights data</p>
        <p className="text-red-500 text-sm mt-1">{error.message}</p>
      </div>
    );
  }

  if (!fh) return null;

  return (
    <div className="space-y-6">
      {/* Executive Summary */}
      <ExecutiveSummaryPanel tab="financial_health" financialHealth={fh} />

      {/* DTI Segment Warning Banner */}
      {fh.dti_segment_warning && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <FiAlertTriangle className="text-amber-500 flex-shrink-0" size={16} />
          <span className="text-sm text-amber-700 font-medium">
            DTI segment classification under review
          </span>
        </div>
      )}

      {/* Header + Data Sufficiency */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-base sm:text-lg font-medium text-slate-700">
            Track member financial wellness and satisfaction improvements
          </h3>
          <DataSourceBadge status="live" source="lender_financial_health_v" />
        </div>

        {/* Key Metrics Grid — LIVE + NOT_CONNECTED */}
        <DataSufficiencyWrapper
          sufficiency={fh.avg_verascore.sufficiency}
          sampleSize={fh.avg_verascore.sample_size}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <LiveMetricCard
              title="Avg VeraScore"
              metric={fh.avg_verascore}
              icon={<FiActivity className="text-slate-600" size={20} />}
              format="score"
              target="70"
              metricKey="financial_health.avg_verascore"
            />
            <LiveMetricCard
              title="Score Consistency"
              metric={fh.score_consistency}
              icon={<FiCheckCircle className="text-slate-600" size={20} />}
              format="percent"
              requiredIntegration="algorithm"
              metricKey="financial_health.score_consistency"
            />
            <LiveMetricCard
              title="Member Satisfaction"
              metric={fh.member_satisfaction}
              icon={<FiStar className="text-slate-600" size={20} />}
              format="ratio"
              requiredIntegration="survey"
              metricKey="financial_health.member_satisfaction"
            />
            <LiveMetricCard
              title="Stress Reduction"
              metric={fh.stress_reduction}
              icon={<FiHeart className="text-slate-600" size={20} />}
              format="percent"
              requiredIntegration="survey"
              metricKey="financial_health.stress_reduction"
            />
            {/* P2B: High Financial Stress — from survey behavioral data */}
            <div className="flex flex-col">
              <LiveMetricCard
                title="High Financial Stress"
                metric={fh.high_financial_stress_count}
                icon={<FiAlertCircle className="text-slate-600" size={20} />}
                format="number"
                requiredIntegration="survey"
                metricKey="financial_health.high_financial_stress_count"
              />
              {/* P2C: CTA — navigate to VS Actions when count > 0 */}
              {fh.high_financial_stress_count.status === 'live' &&
                fh.high_financial_stress_count.value != null &&
                fh.high_financial_stress_count.value > 0 && (
                <button
                  onClick={() => {
                    const url = new URL(window.location.href);
                    url.searchParams.set('view', 'actions');
                    url.searchParams.set('source', 'vs_insights');
                    url.searchParams.set('segment_key', 'high_financial_stress');
                    url.searchParams.set('metric_key', 'high_financial_stress_count');
                    window.history.pushState({}, '', url.toString());
                    window.dispatchEvent(new Event('popstate'));
                  }}
                  className="mt-2 flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors group"
                >
                  View Actions
                  <FiArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
              )}
            </div>
            {/* P4C: Declining VeraScore — CoreMath signal */}
            <div className="flex flex-col">
              <LiveMetricCard
                title="Declining Scores (90d)"
                metric={fh.declining_score_90d_count}
                icon={<FiTrendingDown className="text-slate-600" size={20} />}
                format="number"
                metricKey="financial_health.declining_score_90d_count"
              />
              {/* P4C: Zero-state + tooltip */}
              <div className="mt-2 px-1">
                <div className="flex items-start gap-1.5">
                  <FiInfo className="text-slate-400 flex-shrink-0 mt-0.5" size={12} />
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Members whose VeraScore declined ≥15 points over the past 90 days.
                  </p>
                </div>
                {fh.declining_score_90d_count.value === 0 && (
                  <p className="text-xs text-slate-400 mt-1 italic">
                    No members currently meet this condition. This signal will activate when members show sustained score decline.
                  </p>
                )}
                {fh.declining_score_90d_count.value != null &&
                  fh.declining_score_90d_count.value > 0 && (
                  <button
                    onClick={() => {
                      const url = new URL(window.location.href);
                      url.searchParams.set('view', 'actions');
                      url.searchParams.set('source', 'vs_insights');
                      url.searchParams.set('segment_key', 'declining_score_90d');
                      url.searchParams.set('metric_key', 'declining_score_90d_count');
                      window.history.pushState({}, '', url.toString());
                      window.dispatchEvent(new Event('popstate'));
                    }}
                    className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors group"
                  >
                    View Actions
                    <FiArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                  </button>
                )}
              </div>
            </div>
            {/* P4C-2A: High DTI Stress — CoreMath signal */}
            <div className="flex flex-col">
              <LiveMetricCard
                title="High DTI Stress (>40%)"
                metric={fh.dti_high_count}
                icon={<FiAlertTriangle className="text-slate-600" size={20} />}
                format="number"
                metricKey="financial_health.dti_high_count"
              />
              {/* P4C-2A: Zero-state + tooltip */}
              <div className="mt-2 px-1">
                <div className="flex items-start gap-1.5">
                  <FiInfo className="text-slate-400 flex-shrink-0 mt-0.5" size={12} />
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Members with a debt-to-income ratio exceeding 40%, indicating elevated financial stress.
                  </p>
                </div>
                {fh.dti_high_count.value === 0 && (
                  <p className="text-xs text-slate-400 mt-1 italic">
                    No members currently meet this condition. This signal will activate when members exceed the 40% DTI threshold.
                  </p>
                )}
                {fh.dti_high_count.value != null &&
                  fh.dti_high_count.value > 0 && (
                  <button
                    onClick={() => {
                      const url = new URL(window.location.href);
                      url.searchParams.set('view', 'actions');
                      url.searchParams.set('source', 'vs_insights');
                      url.searchParams.set('segment_key', 'dti_high');
                      url.searchParams.set('metric_key', 'dti_high_count');
                      window.history.pushState({}, '', url.toString());
                      window.dispatchEvent(new Event('popstate'));
                    }}
                    className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors group"
                  >
                    View Actions
                    <FiArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                  </button>
                )}
              </div>
            </div>
            {/* P4C-2B: Low Liquid Reserves — CoreMath signal */}
            <div className="flex flex-col">
              <LiveMetricCard
                title="Low Liquid Reserves (<1 mo)"
                metric={fh.liquidity_months_critical_count}
                icon={<FiDroplet className="text-slate-600" size={20} />}
                format="number"
                metricKey="financial_health.liquidity_months_critical_count"
              />
              {/* P4C-2B: Zero-state + tooltip */}
              <div className="mt-2 px-1">
                <div className="flex items-start gap-1.5">
                  <FiInfo className="text-slate-400 flex-shrink-0 mt-0.5" size={12} />
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Members with less than 1 month of liquid reserves, indicating limited financial cushion.
                  </p>
                </div>
                {fh.liquidity_months_critical_count.value === 0 && (
                  <p className="text-xs text-slate-400 mt-1 italic">
                    No members currently meet this condition. This signal will activate when members fall below the 1-month reserves threshold.
                  </p>
                )}
                {fh.liquidity_months_critical_count.value != null &&
                  fh.liquidity_months_critical_count.value > 0 && (
                  <button
                    onClick={() => {
                      const url = new URL(window.location.href);
                      url.searchParams.set('view', 'actions');
                      url.searchParams.set('source', 'vs_insights');
                      url.searchParams.set('segment_key', 'liquidity_months_critical');
                      url.searchParams.set('metric_key', 'liquidity_months_critical_count');
                      window.history.pushState({}, '', url.toString());
                      window.dispatchEvent(new Event('popstate'));
                    }}
                    className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors group"
                  >
                    View Actions
                    <FiArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </DataSufficiencyWrapper>
      </div>

      {/* Portfolio Health Index — secondary highlight */}
      {phi && phi.status !== 'not_connected' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <LiveMetricCard
            title="Portfolio Health Index"
            metric={phi}
            icon={<FiShield className="text-slate-600" size={20} />}
            format="score"
            target="70"
          />
        </div>
      )}

      {/* VeraScore Trends (LIVE) & Member Satisfaction Trend (NOT_CONNECTED) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base sm:text-lg font-medium text-slate-700">VeraScore Trends</h4>
            <DataSourceBadge status="live" source="lender_financial_health_v" compact />
          </div>
          <DataSufficiencyWrapper
            sufficiency={fh.avg_verascore.sufficiency}
            sampleSize={fh.avg_verascore.sample_size}
          >
            <div className="h-96 sm:h-80">
              <LaborScoreTrendsChart series={fh.score_trend_chart} />
            </div>
          </DataSufficiencyWrapper>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Member Satisfaction Trend</h4>
          <DataSourceNotConnected
            info={NOT_CONNECTED_REGISTRY.survey}
            metricName="Member Satisfaction Trend"
          />
        </div>
      </div>

      {/* Score Distribution (LIVE) & Financial Health Radar (LIVE — 3 axes) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base sm:text-lg font-medium text-slate-700">Score Distribution</h4>
            <DataSourceBadge status="live" source="lender_financial_health_v" compact />
          </div>
          <DataSufficiencyWrapper
            sufficiency={fh.avg_verascore.sufficiency}
            sampleSize={fh.avg_verascore.sample_size}
          >
            <div className="h-96 sm:h-80">
              <ScoreDistributionChart bands={fh.score_distribution} />
            </div>
          </DataSufficiencyWrapper>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base sm:text-lg font-medium text-slate-700">Financial Health Factors</h4>
            <DataSourceBadge status="live" source="lender_financial_health_v" compact />
          </div>
          <DataSufficiencyWrapper
            sufficiency={fh.avg_verascore.sufficiency}
            sampleSize={fh.avg_verascore.sample_size}
          >
            <div className="h-96 sm:h-80">
              <FinancialHealthRadarChart factors={fh.factor_breakdown} />
            </div>
          </DataSufficiencyWrapper>
        </div>
      </div>

      {/* Score Band Migration — LIVE (historical band movement) */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base sm:text-lg font-medium text-slate-700">Score Band Migration</h4>
          <DataSourceBadge status="live" source="lender_financial_health_v" compact />
        </div>
        <DataSufficiencyWrapper
          sufficiency={fh.avg_verascore.sufficiency}
          sampleSize={fh.avg_verascore.sample_size}
        >
          <div className="h-96 sm:h-80">
            <ScoreBandMigrationChart data={fh.score_band_migration} />
          </div>
        </DataSufficiencyWrapper>
      </div>

      {/* Stress Reduction by Category — NOT_CONNECTED (survey) */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
        <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Member Stress Reduction by Category</h4>
        <DataSourceNotConnected
          info={NOT_CONNECTED_REGISTRY.survey}
          metricName="Stress Reduction by Category"
        />
      </div>

      {/* Score Consistency Analysis — NOT_CONNECTED (algorithm) */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
        <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Score Consistency Analysis</h4>
        <DataSourceNotConnected
          info={NOT_CONNECTED_REGISTRY.algorithm}
          metricName="Score Consistency Analysis"
        />
      </div>
    </div>
  );
};

export default FinancialHealth;